import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { memo, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  IconBolt,
  IconBookOpen,
  IconLink,
  IconPaperAirplane,
  IconPuzzle,
  IconUserPlus,
  DeviceLink,
  Spinner,
} from '@/components';
import type { ActivityEvent, ActivityKind, ActivityResponse } from '@/lib/admin-types';
import { formatAdminDateTime, formatAdminTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

/** Backend formats run summaries as `scored <N> in <slug>` (see `routes/admin_data.py`).
 *  We split it so the score can be the visual focal point of the card. */
function parseRunSummary(summary: string): { score: number; game: string } | null {
  const m = summary.match(/^scored\s+(-?\d+)\s+in\s+(.+)$/i);
  if (!m) return null;
  return { score: Number(m[1]), game: m[2] };
}

function timeAgo(iso: string) {
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return formatAdminDateTime(iso);
}

function kindBadge(kind: ActivityKind) {
  const map: Record<
    ActivityKind,
    { label: string; variant: 'neutral' | 'primary' | 'success' | 'danger' }
  > = {
    registration: { label: 'Registration', variant: 'success' },
    run: { label: 'Game run', variant: 'primary' },
    push: { label: 'Push', variant: 'neutral' },
    unsubscribe: { label: 'Notifications off', variant: 'danger' },
    resource_tap: { label: 'Resource tap', variant: 'neutral' },
    flashcard_view: { label: 'Flashcard', variant: 'primary' },
  };
  const m = map[kind];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

const kindVisual: Record<
  ActivityKind,
  { Icon: typeof IconPuzzle; accent: string; iconWrap: string; iconClass: string }
> = {
  registration: {
    Icon: IconUserPlus,
    accent: 'bg-emerald-500',
    iconWrap: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60',
    iconClass: 'text-emerald-700',
  },
  run: {
    Icon: IconPuzzle,
    accent: 'bg-sky-500',
    iconWrap: 'bg-sky-50 text-sky-700 ring-sky-200/60',
    iconClass: 'text-sky-700',
  },
  push: {
    Icon: IconPaperAirplane,
    accent: 'bg-violet-500',
    iconWrap: 'bg-violet-50 text-violet-700 ring-violet-200/60',
    iconClass: 'text-violet-700',
  },
  unsubscribe: {
    Icon: IconBolt,
    accent: 'bg-rose-500',
    iconWrap: 'bg-rose-50 text-rose-700 ring-rose-200/60',
    iconClass: 'text-rose-700',
  },
  resource_tap: {
    Icon: IconLink,
    accent: 'bg-amber-500',
    iconWrap: 'bg-amber-50 text-amber-800 ring-amber-200/60',
    iconClass: 'text-amber-800',
  },
  flashcard_view: {
    Icon: IconBookOpen,
    accent: 'bg-teal-500',
    iconWrap: 'bg-teal-50 text-teal-800 ring-teal-200/60',
    iconClass: 'text-teal-800',
  },
};

function eventStableKey(e: ActivityEvent): string {
  return `${e.kind}|${e.installation_id}|${e.at}|${e.summary}`;
}

const ActivityEventRow = memo(function ActivityEventRow({
  e,
  enableMotion,
}: {
  e: ActivityEvent;
  enableMotion: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const v = kindVisual[e.kind];
  const Icon = v.Icon;
  const run = e.kind === 'run' ? parseRunSummary(e.summary) : null;
  const motionOn = enableMotion && !reduceMotion;

  return (
    <motion.li
      layout
      initial={motionOn ? { opacity: 0, y: -16, scale: 0.985 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={
        motionOn
          ? { opacity: 0, scale: 0.97, transition: { duration: 0.14, ease: 'easeOut' } }
          : { opacity: 1, transition: { duration: 0 } }
      }
      transition={{
        layout: { type: 'spring', stiffness: 420, damping: 34, mass: 0.85 },
        opacity: { duration: motionOn ? 0.2 : 0 },
        y: { type: 'spring', stiffness: 440, damping: 36, mass: 0.8 },
        scale: { duration: motionOn ? 0.2 : 0 },
      }}
      className="group relative overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm ring-1 ring-gray-900/[0.04] will-change-transform hover:border-primary/20 hover:shadow-md"
      style={{ listStyle: 'none' }}
    >
      <div className={clsx('absolute left-0 top-0 h-full w-1 rounded-l-2xl', v.accent)} aria-hidden />
      <div className="flex gap-4 p-4 pl-5 sm:items-start">
        <div
          className={clsx(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1 ring-inset',
            v.iconWrap
          )}
        >
          <Icon size={22} className={v.iconClass} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {kindBadge(e.kind)}
            {(e.nickname || e.timezone) && (
              <span
                className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                title={e.timezone ?? undefined}
              >
                {e.nickname && (
                  <DeviceLink
                    installationId={e.installation_id}
                    nickname={e.nickname}
                    label="nickname"
                    className="truncate font-medium text-gray-700 hover:text-primary"
                  />
                )}
                {e.timezone && (
                  <span className="font-mono text-[11px] font-normal text-gray-500">
                    {e.nickname ? `· ${e.timezone}` : e.timezone}
                  </span>
                )}
              </span>
            )}
          </div>
          {run ? (
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-2xl font-semibold leading-none tracking-tight tabular-nums text-sky-700">
                {run.score.toLocaleString()}
              </span>
              <span className="text-[13px] text-gray-500">
                {run.score === 1 ? 'point in' : 'points in'}
              </span>
              <span className="rounded-md bg-sky-50 px-1.5 py-0.5 font-mono text-[12px] font-medium text-sky-800 ring-1 ring-inset ring-sky-200/60">
                {run.game}
              </span>
            </div>
          ) : (
            <p className="mt-2 text-[15px] font-medium leading-snug text-gray-900">{e.summary}</p>
          )}
          <p className="mt-1.5 truncate" title={e.installation_id}>
            <DeviceLink
              installationId={e.installation_id}
              label="id"
              className="font-mono text-[11px] text-gray-400 hover:text-primary"
            />
          </p>
        </div>
        <div className="shrink-0 pt-1 text-right">
          <p className="text-xs font-medium tabular-nums text-gray-500">{timeAgo(e.at)}</p>
        </div>
      </div>
    </motion.li>
  );
});

function ActivityPage() {
  const [filter, setFilter] = useState<ActivityKind | 'all'>('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [, setTimeTick] = useState(0);
  const reduceMotion = useReducedMotion();
  const [listMotionEnabled, setListMotionEnabled] = useState(false);

  const q = useQuery({
    queryKey: ['admin', 'activity', 'recent'],
    queryFn: () => apiJson<ActivityResponse>('/v1/admin/activity/recent?limit=80'),
    refetchInterval: autoRefresh ? 5_000 : false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    const id = window.setInterval(() => {
      setTimeTick((n) => n + 1);
    }, 1_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!q.isSuccess) return;
    const id = window.requestAnimationFrame(() => {
      setListMotionEnabled(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [q.isSuccess]);

  const items = useMemo(() => {
    const all = q.data?.items ?? [];
    if (filter === 'all') return all;
    return all.filter((e) => e.kind === filter);
  }, [q.data, filter]);

  const chips: Array<{ id: typeof filter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'registration', label: 'Registrations' },
    { id: 'run', label: 'Runs' },
    { id: 'push', label: 'Pushes' },
    { id: 'unsubscribe', label: 'Opt-outs' },
    { id: 'resource_tap', label: 'Resources' },
    { id: 'flashcard_view', label: 'Flashcards' },
  ];

  const updatedLabel =
    q.dataUpdatedAt > 0
      ? `Updated ${formatAdminTime(q.dataUpdatedAt)}`
      : q.isFetching
        ? 'Updating…'
        : '';

  const showFetchBar = q.isFetching && items.length > 0 && !reduceMotion;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Activity</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Live feed of registrations, game runs, pushes, opt-outs, resource taps, and flashcard views. Filter by
            type or turn on{' '}
            <span className="font-medium text-gray-700">live refresh</span> to poll every 5 seconds.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300">
            <input
              type="checkbox"
              role="switch"
              aria-checked={autoRefresh}
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="flex items-center gap-1.5">
              {autoRefresh && (
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
              Live (5s)
            </span>
          </label>
          <Button variant="secondary" size="sm" onClick={() => void q.refetch()} disabled={q.isFetching}>
            {q.isFetching ? 'Refreshing…' : 'Refresh now'}
          </Button>
          {updatedLabel && <span className="text-xs text-gray-400">{updatedLabel}</span>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setFilter(c.id)}
            className={clsx(
              'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
              filter === c.id
                ? 'bg-primary text-white shadow-md shadow-primary/25 ring-2 ring-primary/20'
                : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200/80 hover:bg-gray-200/90'
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      <Card
        padding="md"
        className={clsx(
          'relative overflow-hidden border-gray-200/90 bg-gradient-to-b from-gray-50/80 to-white',
          showFetchBar && 'ring-1 ring-primary/15'
        )}
      >
        {showFetchBar && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[2px] overflow-hidden bg-primary/10"
            aria-hidden
          >
            <motion.div
              className="absolute top-0 h-full w-[28%] rounded-full bg-primary/75 shadow-[0_0_12px_rgba(15,138,138,0.45)]"
              initial={{ left: '-30%' }}
              animate={{ left: '105%' }}
              transition={{ duration: 1.15, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        )}

        {q.isLoading && !q.data && (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        )}
        {q.isError && (
          <p className="text-sm text-red-600">
            {(q.error as Error).message}
            <button type="button" className="ml-2 underline" onClick={() => void q.refetch()}>
              Retry
            </button>
          </p>
        )}
        {q.isSuccess && items.length === 0 && (
          <div className="py-14 text-center">
            <p className="text-sm font-medium text-gray-600">No events match this filter</p>
            <p className="mt-1 text-xs text-gray-400">Try “All” or switch live refresh on to catch new traffic.</p>
          </div>
        )}
        {q.isSuccess && items.length > 0 && (
          <ul className="flex flex-col gap-3" role="list" aria-busy={q.isFetching} aria-live="polite">
            <AnimatePresence mode="popLayout" initial={false}>
              {items.map((e: ActivityEvent) => (
                <ActivityEventRow key={eventStableKey(e)} e={e} enableMotion={listMotionEnabled} />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/activity')({
  component: ActivityPage,
});
